// Customer history (office-history prototype; Q19, Q20): the shapes the screen reads and how it
// groups and words them.
import { jobStatusWord } from '@flowology/ui-web/words';

export type EventKind = 'job' | 'invoice' | 'payment' | 'call' | 'message' | 'note';

export interface HistoryEvent {
  kind: EventKind;
  at: string;
  title: string;
  detail: string;
  jobId: string | null;
  invoiceId: string | null;
  locationId: string | null;
  amountCents: number | null;
  status: string | null;
  /** A call's id. */
  callId?: string;
  /**
   * A call that came over from ServiceTitan: its recording and voicemail, each kept (it plays), gone
   * (past the two years they're kept) or none (it never had one). Null on Flowology's own calls.
   */
  stRecording?: { recording: StAudio; voicemail: StAudio } | null;
  /** A call's notes, as saved on it (calls item 6). */
  callNotes?: string | null;
  /** A call with its Twilio recording kept: calls.transcript reads it (BL-083, owner and managers). */
  twilioRecording?: boolean;
  /** A job note: its id and version for notes.update and notes.delete, and whether this person may. */
  note?: { id: string; rowVersion: number; editable: boolean };
  /** The customer's signature accepting the work, on an invoice row (QA-727): who, where, the pad. */
  signature?: { signerName: string; channel: 'device' | 'portal'; imageUrl: string | null };
}

export type StAudio = 'kept' | 'gone' | 'none';

/**
 * What a ServiceTitan call offers (QA-520): Play for what is kept, "no longer kept" for what was
 * there and is past its two years, and nothing at all for a call that never had audio.
 */
export function playable(e: Pick<HistoryEvent, 'stRecording'>): {
  recording: boolean;
  voicemail: boolean;
  /** The words for what was there and isn't kept any more; empty when nothing is gone. */
  gone: string[];
} | null {
  const s = e.stRecording;
  if (!s) return null;
  const gone = [
    ...(s.recording === 'gone' ? ['The ServiceTitan recording is no longer kept (two years).'] : []),
    ...(s.voicemail === 'gone' ? ['The ServiceTitan voicemail is no longer kept (two years).'] : []),
  ];
  const out = { recording: s.recording === 'kept', voicemail: s.voicemail === 'kept', gone };
  return out.recording || out.voicemail || gone.length ? out : null;
}

export interface History {
  customerId: string;
  summary: {
    /** The jobs visited (a visit not cancelled, the job not cancelled). */
    visits: number;
    /** Their visits: the appointments not cancelled (history fix 4). */
    appointments?: number;
    firstVisit: string | null;
    topJobTypes: { name: string; count: number }[];
    /** Not sent yet: shown once customers.history's summary carries them. */
    usualTech?: { name: string; visits: number } | null;
    equipment?: { kind: string; installedYear: number | null }[];
  };
  events: HistoryEvent[];
}

/** A card charge the processor hasn't answered about, on the invoice it holds (customers.money, invoices.get). */
export interface CardCheck {
  checkoutId: string;
  amountCents?: number;
  startedAt?: string;
  cardLast4?: string | null;
}

export interface Money {
  openCents: number;
  overdueCents: number;
  paidCents: number;
  invoices: {
    id: string;
    number: string;
    status: string;
    issuedOn: string | null;
    dueOn: string | null;
    jobNumber: string | null;
    totalCents: number;
    balanceCents: number;
    /** What was written off as a bad debt (a written-off invoice reads "written_off"). */
    writtenOffCents?: number;
    overdue: boolean;
    /** A card charge on it still being checked: nothing else is taken on it until it is (QA-307). */
    cardBeingChecked?: CardCheck | null;
  }[];
  payments:
    | {
        id: string;
        receivedAt: string;
        method: string;
        amountCents: number;
        status: string;
        /** Why it was voided (payments.void); null otherwise. */
        voidReason: string | null;
        /** What can still be given back: Refund is capped at it, and hidden at 0. */
        refundableCents?: number;
        /** Its receipt: sent, or why not; null when it never had one (an import, or before receipts). */
        receipt?: PaymentReceipt | null;
      }[]
    | null;
  creditCents: number | null;
  /** Where a receipt sent now would go (Send again), or null: do-not-contact or no address they agreed to. */
  receiptChannel?: 'email' | 'sms' | null;
}

export interface PaymentReceipt {
  outcome: 'sent' | 'skipped' | 'held';
  reason:
    'off' | 'void' | 'refunded' | 'do_not_contact' | 'no_consented_address' | 'no_invoice' | 'renewal' | null;
  channel: 'email' | 'sms' | null;
  at: string;
  failed: boolean;
}

export interface AuditEntry {
  id: string;
  at: string;
  who: string;
  whoKind: string;
  source: string;
  action: string;
  title: string;
  entityType: string;
  fields: string[];
}

export const KIND_LABEL: Record<EventKind, string> = {
  job: 'Jobs',
  invoice: 'Invoices',
  payment: 'Payments',
  call: 'Calls',
  message: 'Texts & emails',
  note: 'Notes',
};
export const KIND_CHIP: Record<EventKind, string> = {
  job: 'brand',
  invoice: 'warn',
  payment: 'ok',
  call: '',
  message: '',
  note: '',
};

const dateFmt = (timeZone: string) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
const timeFmt = (timeZone: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone, hour: 'numeric', minute: '2-digit' });

/** Events grouped under their company-local day, newest day first (the order they arrive in). */
export function byDay(
  events: readonly HistoryEvent[],
  timeZone: string,
): { day: string; events: HistoryEvent[] }[] {
  const out: { day: string; events: HistoryEvent[] }[] = [];
  const f = dateFmt(timeZone);
  for (const e of events) {
    const day = f.format(new Date(e.at));
    const last = out[out.length - 1];
    if (last?.day === day) last.events.push(e);
    else out.push({ day, events: [e] });
  }
  return out;
}

export const timeOf = (at: string, timeZone: string) => timeFmt(timeZone).format(new Date(at));

/**
 * A row's line under its title. A job's ends in its status as the server keeps it ("No heat ·
 * awaiting review"); that end says the app's one word for it instead ("No heat · Waiting for review").
 */
export function eventDetail(e: Pick<HistoryEvent, 'kind' | 'detail' | 'status'>): string {
  if (e.kind !== 'job' || !e.status) return e.detail;
  const raw = ` · ${e.status.replace(/_/g, ' ')}`;
  return e.detail.endsWith(raw) ? `${e.detail.slice(0, -raw.length)} · ${jobStatusWord(e.status)}` : e.detail;
}

/**
 * "9 jobs · 12 visits": the jobs visited and their visits (history fix 4: the header counted jobs as
 * visits); "9 jobs" before the server says how many visits.
 */
export function jobsAndVisits(s: Pick<History['summary'], 'visits' | 'appointments'>): string {
  const n = (k: number, w: string) => `${k.toLocaleString('en-CA')} ${w}${k === 1 ? '' : 's'}`;
  return s.appointments === undefined
    ? n(s.visits, 'job')
    : `${n(s.visits, 'job')} · ${n(s.appointments, 'visit')}`;
}

/** "We have been to this customer 7 times since 12 Mar 2024. Most often: leak repair ×4, …" (each visit a time) */
export function summaryLine(s: History['summary'], formatDate: (d: string) => string): string {
  if (!s.visits) return 'No visits yet.';
  const top = s.topJobTypes.map((t) => `${t.name.toLowerCase()} ×${t.count}`).join(', ');
  const times = s.appointments ?? s.visits;
  return `We have been here ${times} time${times === 1 ? '' : 's'}${
    s.firstVisit ? ` since ${formatDate(s.firstVisit)}` : ''
  }.${top ? ` Most often: ${top}.` : ''}`;
}

/**
 * "What we've done at this address" (office-history prototype): from the address's jobs (a
 * customers.history of kind job, for one location or every one) and, when the role sees money, the
 * customer's invoices, kept to that address's jobs.
 */
export interface AddressSummary {
  /** The jobs visited there. */
  visits: number;
  /** Their visits (appointments not cancelled); undefined before the server says. */
  appointments?: number;
  /** Company-local YYYY-MM-DD of the first visit. */
  since: string | null;
  mostly: string | null;
  /** Today's job here, by its number. */
  today: string | null;
  last: { date: string; today: boolean; what: string } | null;
  owing: { cents: number; invoices: number } | null;
  usualTech: { name: string; visits: number } | null;
  equipment: { kind: string; installedYear: number | null }[] | null;
}

const dayIn = (at: string | Date, timeZone: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(at),
  );
// A job event's title is "<number> · <job type>".
const numberOf = (e: HistoryEvent) => e.title.split(' · ')[0] ?? e.title;
const typeOf = (e: HistoryEvent) => e.title.split(' · ').slice(1).join(' · ') || e.title;

/** A job's event title as the office writes it: its number as "#N" (937f63c). */
export const jobTitle = (e: HistoryEvent) =>
  e.kind === 'job' && /^\d/.test(e.title) ? `#${e.title}` : e.title;

/**
 * What a row's title opens, as its button's name: the words it shows first, then where it goes, so
 * someone who says what they see (voice control) presses it (WCAG 2.5.3 label in name, QA-881). Null
 * when the row opens nothing.
 */
export function rowOpensName(e: HistoryEvent, customerName: string): string | null {
  const shown = jobTitle(e);
  if (e.kind === 'job' && e.jobId) return `${shown}: open the job`;
  if (e.kind === 'invoice' && e.invoiceId) return `${shown}: open the invoice`;
  if (e.kind === 'message') return `${shown}: open ${customerName}'s messages`;
  return null;
}

/** A row's own button in the older design: its words first, then which row's (QA-881). */
export const rowActionName = (shown: string, e: HistoryEvent) => `${shown}: ${jobTitle(e)}`;

/** The last visit: the latest job that has started (today's, once it has; never a booked one ahead). */
export function lastVisit(events: readonly HistoryEvent[], now = new Date()): HistoryEvent | null {
  return events
    .filter((e) => e.kind === 'job' && e.status !== 'cancelled' && new Date(e.at) <= now)
    .reduce<HistoryEvent | null>((a, e) => (!a || e.at > a.at ? e : a), null);
}

export function addressSummary(
  jobs: History,
  money: Money | null,
  opts: {
    timeZone: string;
    /** an invoice with no job counts too (the whole customer is in view) */ withJobless: boolean;
    now?: Date;
  },
): AddressSummary {
  const now = opts.now ?? new Date();
  const today = dayIn(now, opts.timeZone);
  const live = jobs.events.filter((e) => e.kind === 'job' && e.status !== 'cancelled');
  const lastJob = lastVisit(jobs.events, now);
  const todayJob = live.find((e) => dayIn(e.at, opts.timeZone) === today);
  const here = new Set(jobs.events.filter((e) => e.kind === 'job').map(numberOf));
  const open = money
    ? money.invoices.filter(
        (i) =>
          i.balanceCents > 0 &&
          i.status !== 'void' &&
          i.status !== 'draft' &&
          (i.jobNumber ? here.has(i.jobNumber) : opts.withJobless),
      )
    : null;
  return {
    visits: jobs.summary.visits,
    ...(jobs.summary.appointments !== undefined ? { appointments: jobs.summary.appointments } : {}),
    since: jobs.summary.firstVisit,
    mostly: jobs.summary.topJobTypes[0]?.name ?? null,
    today: todayJob ? numberOf(todayJob) : null,
    last: lastJob
      ? {
          date: dayIn(lastJob.at, opts.timeZone),
          today: dayIn(lastJob.at, opts.timeZone) === today,
          what: typeOf(lastJob),
        }
      : null,
    owing: open ? { cents: open.reduce((t, i) => t + i.balanceCents, 0), invoices: open.length } : null,
    usualTech: jobs.summary.usualTech ?? null,
    equipment: jobs.summary.equipment ?? null,
  };
}

/** What the person sees here, by the permissions they hold (the server enforces it; this says so on the page). */
export function historyRights(held: readonly string[]) {
  const has = (p: string) => held.includes(p);
  return {
    /** audit.view: the owner and managers */
    audit: has('audit.view'),
    /** recordings.play: the owner, and managers (roles.ts) */
    recordings: has('recordings.play'),
    /** receivables.view: a statement of what they owe (the office) */
    statement: has('receivables.view'),
    /** jobs.book */
    book: has('jobs.book'),
    /** messages.send: "Send a text or email" */
    message: has('messages.send'),
    /** customers.create */
    addCustomer: has('customers.create'),
  };
}

/** The default statement period: the last 90 days to today (company-local dates). */
export function statementPeriod(today: string): { from: string; to: string } {
  const d = new Date(`${today}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 90);
  return { from: d.toISOString().slice(0, 10), to: today };
}

/** Turns a camelCase field name into words for the audit trail ("accessNotes" → "access notes"). */
export const fieldWords = (f: string) => f.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();

// An audit row says what was done in the past tense ("Booked a job"), from the action's own title
// ("Book a job"); only the first word changes.
const IRREGULAR: Record<string, string> = {
  Send: 'Sent',
  Pay: 'Paid',
  Set: 'Set',
  Give: 'Gave',
  Take: 'Took',
  Get: 'Got',
  Read: 'Read',
  Put: 'Put',
};
export function pastTense(title: string): string {
  const m = /^([A-Z][a-z]+)(\b.*)$/.exec(title);
  if (!m) return title;
  const [, w, rest] = m as unknown as [string, string, string];
  if (/ed$/.test(w)) return title; // already past ("Changed")
  const past =
    IRREGULAR[w] ??
    (/e$/.test(w)
      ? `${w}d`
      : /[^aeiou]y$/.test(w)
        ? `${w.slice(0, -1)}ied`
        : /^(Cancel)$/.test(w)
          ? `${w}led`
          : /[^aeiou][aeiou][^aeiouwxy]$/i.test(w) && (w.match(/[aeiou]+/gi) ?? []).length === 1
            ? `${w}${w.at(-1)}ed`
            : `${w}ed`);
  return past + rest;
}

/**
 * Where an audited change came from, in words. The office app goes unsaid, and so does the system:
 * its rows already name who it acted for (a seeded or scheduled change), and "by Flowology" beside a
 * person's name would contradict it.
 */
export const SOURCE_WORDS: Record<string, string | null> = {
  web: null,
  device: 'from the iPad',
  agent: 'by an agent',
  mcp: 'from an AI client',
  portal: 'in the customer portal',
  system: null,
};
